import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const BlogPage = () => {
  const navigate = useNavigate();
  const [selectedGem, setSelectedGem] = useState('emerald-guide'); // Default to Emerald

  const gemstones = [
    {
      title: 'Emerald',
      subtitle: 'The Stone of Royalty & Rebirth',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/57qw7w56_F683E346-12CC-4A47-A850-08225E60D944.png',
      slug: 'emerald-guide'
    },
    {
      title: 'Ruby',
      subtitle: 'The Flame of Passion & Power',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/qm5wph25_B13B369F-7304-410A-A966-07E267DEAF7E.png',
      slug: 'ruby-guide'
    },
    {
      title: 'Sapphire',
      subtitle: 'The Gem of Wisdom & Nobility',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/8627k2of_A3D9625E-612F-4417-A540-193F15E44D53.png',
      slug: 'sapphire-guide'
    }
  ];

  const gemDetails = {
    'emerald-guide': {
      title: 'Emerald',
      subtitle: 'The Stone of Royalty & Rebirth',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/57qw7w56_F683E346-12CC-4A47-A850-08225E60D944.png',
      content: [
        {
          heading: 'Origins We Offer',
          items: [
            '<strong>Zambia</strong> — Vivid deep greens, exceptional clarity for emeralds, strong saturation',
            '<strong>Colombia</strong> — World\'s most famous origin, glowing "green fire," extremely rare',
            '<strong>Panjshir Valley, Afghanistan</strong> — Legendary source of mesmerizing emeralds with sublime crystalline structure',
            '<strong>Brazil</strong> — Lighter greens, excellent brilliance, great value'
          ]
        },
        {
          heading: 'Why Emeralds Are Exceptional',
          items: [
            'Known as the "King of Green."',
            'Associated with renewal, abundance, and intuition.',
            'Historically worn by Egyptian pharaohs, Mughal emperors, and European royalty.',
            'Highly collectible due to their natural inclusions (called jardin), which make every emerald unique.'
          ]
        },
        {
          heading: 'Why Collectors Love Emeralds',
          text: 'Emeralds are appreciating assets—top-quality stones from Zambia and Colombia have surged in value over the past decade. Their rarity, especially when untreated, makes them exceptionally desirable.'
        }
      ]
    },
    'ruby-guide': {
      title: 'Ruby',
      subtitle: 'The Flame of Passion & Power',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/qm5wph25_B13B369F-7304-410A-A966-07E267DEAF7E.png',
      content: [
        {
          heading: 'Origins We Offer',
          items: [
            '<strong>Burma (Myanmar)</strong> — The legendary "pigeon blood" rubies',
            '<strong>Mozambique</strong> — Modern high-quality rubies with rich red saturation',
            '<strong>Thailand</strong> — Deep, garnet-like reds, ideal for jewelry'
          ]
        },
        {
          heading: 'Why Rubies Are Exceptional',
          items: [
            'Long considered the most powerful gemstone.',
            'Symbolizes love, vitality, and protection.',
            'Burmese rubies are among the world\'s most expensive gems—top pieces outprice diamonds.'
          ]
        },
        {
          heading: 'Why Collectors Love Rubies',
          text: 'Rubies have a near-mythical status. A fine ruby is significantly rarer than a diamond, making it an extraordinary heirloom and investment.'
        }
      ]
    },
    'sapphire-guide': {
      title: 'Sapphire',
      subtitle: 'The Gem of Wisdom & Nobility',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/8627k2of_A3D9625E-612F-4417-A540-193F15E44D53.png',
      content: [
        {
          heading: 'Origins We Offer',
          items: [
            '<strong>Sri Lanka (Ceylon)</strong> — Bright, velvety blues; highly prized',
            '<strong>Kashmir</strong> — Extremely rare, cornflower blue, museum-grade',
            '<strong>Madagascar</strong> — Modern source with vivid saturation and clarity'
          ]
        },
        {
          heading: 'Why Sapphires Are Exceptional',
          items: [
            'Associated with wisdom, truth, and calm.',
            'Historically gifted to royalty and featured in crown jewels.',
            'Available in many colors—pink, yellow, teal, purple—each with beauty and personality.'
          ]
        },
        {
          heading: 'Why Collectors Love Sapphires',
          text: 'Ceylon and Kashmir sapphires continue to rise in value due to rarity and demand. Their durability and brilliance make them perfect for both investment and daily wear.'
        }
      ]
    }
  };

  const selectedGemData = gemDetails[selectedGem];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="py-12 md:py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4 md:mb-6">The Gem Atelier Education Hub</h1>
            <div className="max-w-3xl mx-auto">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-3 md:mb-4">
                Understanding gemstones transforms appreciation into connection.
              </p>
              <p className="text-base md:text-lg text-gray-600">
                Below is a refined guide to the world's most sought-after colored gems—what makes them special, how to choose them, and why they remain treasured across civilizations.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gemstone Selector */}
      <section className="py-8 md:py-12 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl text-center mb-8">Select a Gemstone to Learn More</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
            {gemstones.map((gem, index) => (
              <motion.button
                key={gem.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onClick={() => setSelectedGem(gem.slug)}
                className={`group cursor-pointer bg-white border-2 transition-all overflow-hidden rounded-lg ${
                  selectedGem === gem.slug
                    ? 'border-black shadow-lg scale-105'
                    : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
                }`}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={gem.image}
                    alt={gem.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="font-serif text-lg md:text-xl mb-1">{gem.title}</h3>
                  <p className="text-xs md:text-sm text-gray-600">{gem.subtitle}</p>
                  {selectedGem === gem.slug && (
                    <div className="mt-2 text-xs text-green-600 font-medium">✓ Selected</div>
                  )}
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Gem Details */}
      <AnimatePresence mode="wait">
        {selectedGemData && (
          <motion.section
            key={selectedGem}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="py-12 md:py-20 px-4 bg-white"
          >
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
                {/* Image */}
                <div className="relative">
                  <img
                    src={selectedGemData.image}
                    alt={selectedGemData.title}
                    className="w-full aspect-square object-cover rounded-lg shadow-lg"
                  />
                </div>

                {/* Content */}
                <div>
                  <h2 className="font-serif text-3xl md:text-4xl mb-2">{selectedGemData.title}</h2>
                  <p className="text-lg text-gray-600 mb-6">{selectedGemData.subtitle}</p>

                  {selectedGemData.content.map((section, index) => (
                    <div key={index} className="mb-6">
                      <h3 className="font-medium text-lg mb-3">{section.heading}</h3>
                      
                      {section.items && (
                        <ul className="space-y-2">
                          {section.items.map((item, i) => (
                            <li key={i} className="flex items-start text-sm text-gray-700">
                              <span className="w-1.5 h-1.5 bg-black rounded-full mr-3 mt-2 flex-shrink-0"></span>
                              <span dangerouslySetInnerHTML={{ __html: item }}></span>
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      {section.text && (
                        <p className="text-sm text-gray-700 leading-relaxed">{section.text}</p>
                      )}
                    </div>
                  ))}

                  <div className="flex gap-4 mt-8">
                    <Button
                      onClick={() => navigate(`/shop?gem_type=${selectedGemData.title.toLowerCase().replace('panjshir ', '')}`)}
                      className="bg-black text-white hover:bg-gray-900"
                    >
                      Browse {selectedGemData.title}s
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/blog/${selectedGem}`)}
                      className="border-black"
                    >
                      Full Guide
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* General Guides */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl mb-8">How to Choose the Right Gemstone</h2>
            
            <div className="space-y-6 text-lg text-gray-700">
              <p className="font-medium text-center">Consider:</p>
              
              <div className="space-y-4 max-w-2xl mx-auto">
                <div>
                  <span className="font-medium">Origin</span> — The birthplace of beauty and value
                </div>
                <div>
                  <span className="font-medium">Treatment</span> — Untreated stones always command a premium
                </div>
                <div>
                  <span className="font-medium">Color</span> — Purity and saturation define desirability
                </div>
                <div>
                  <span className="font-medium">Clarity</span> — Nature's fingerprint; acceptable varies by gem type
                </div>
                <div>
                  <span className="font-medium">Cut</span> — Symmetry maximizes brilliance
                </div>
                <div>
                  <span className="font-medium">Carat</span> — Larger gems become exponentially rarer
                </div>
              </div>

              <p className="italic pt-4 text-center">
                At The Gem Atelier, each stone is chosen to reflect excellence across these factors.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Own Section */}
      <section className="py-20 px-4 bg-black text-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl mb-12 text-center">Why Own a Gemstone?</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="font-serif text-2xl mb-3">1. Timeless Beauty</h3>
                <p className="text-gray-300 text-lg">
                  Unlike technology or fashion, gemstones never depreciate aesthetically.
                </p>
              </div>

              <div>
                <h3 className="font-serif text-2xl mb-3">2. Emotional Expression</h3>
                <p className="text-gray-300 text-lg">
                  Each gem carries symbolism—love, strength, peace, prosperity.
                </p>
              </div>

              <div>
                <h3 className="font-serif text-2xl mb-3">3. Investment Value</h3>
                <p className="text-gray-300 text-lg">
                  Fine gems are finite; mines deplete, rarity increases, demand grows.
                </p>
              </div>

              <div>
                <h3 className="font-serif text-2xl mb-3">4. A Legacy Piece</h3>
                <p className="text-gray-300 text-lg">
                  A gemstone lives beyond you—becoming a family heirloom.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl mb-6">Your Journey Starts Here</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Whether discovering your first emerald or expanding a curated collection,
              The Gem Atelier exists to guide you with expertise, integrity, and passion.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
